import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { BUSINESS_KIND, BUSINESS_TAG, parseBusinessEvent } from '@/lib/business';
import { BLOCKED_PUBKEYS } from '@/lib/admin';
import { discoveryPool, discoverySignal } from '@/lib/appRelays';
import type { Merchant } from '@/lib/merchants';

/**
 * Fetch BCHNostr business directory listings (kind 30499, tagged `bch-business`)
 * and map them into the app's `Merchant` shape.
 *
 * These are addressable events, so we keep only the latest event per pubkey.
 */
export function useBusinessListings() {
  return useQuery({
    queryKey: ['nostr', 'merchants', 'bchnostr-businesses'],
    queryFn: async (c) => {
      const events = await discoveryPool.query(
        [{ kinds: [BUSINESS_KIND], '#t': [BUSINESS_TAG], limit: 500 }],
        { signal: discoverySignal(c.signal) },
      );

      // Keep the latest event per pubkey.
      const latestByPubkey = new Map<string, (typeof events)[number]>();
      for (const event of events) {
        if (BLOCKED_PUBKEYS.has(event.pubkey)) continue;
        const existing = latestByPubkey.get(event.pubkey);
        if (!existing || event.created_at > existing.created_at) {
          latestByPubkey.set(event.pubkey, event);
        }
      }

      const merchants: Merchant[] = [];
      for (const event of latestByPubkey.values()) {
        const merchant = parseBusinessEvent(event);
        if (merchant) {
          merchants.push(merchant);
        }
      }

      return merchants;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Keep the previous results visible during a refetch so the count never
    // drops to a partial set while relays are re-responding.
    placeholderData: keepPreviousData,
    // Union fresh results with the previous set (by pubkey, newest wins) so a
    // temporarily-incomplete relay response never removes known businesses.
    structuralSharing: (prev, next) =>
      unionBusinesses(prev as Merchant[] | undefined, next as Merchant[]),
  });
}

function unionBusinesses(prev: Merchant[] | undefined, next: Merchant[]): Merchant[] {
  if (!prev || prev.length === 0) return next;

  const byPubkey = new Map<string, Merchant>();
  for (const m of prev) byPubkey.set(m.pubkey, m);
  for (const m of next) {
    const existing = byPubkey.get(m.pubkey);
    if (!existing || m.event.created_at >= existing.event.created_at) {
      byPubkey.set(m.pubkey, m);
    }
  }
  return [...byPubkey.values()];
}
